import {
  REFERRAL_CHANNELS,
  REFERRAL_DIRECTIONS,
  REFERRAL_EVENT_KINDS,
  REFERRAL_HIE_SHARES,
  REFERRAL_ORIGINS,
  REFERRAL_STATUSES,
  REFERRAL_UPDATE_KINDS,
  REFERRAL_URGENCIES,
} from '@wodi/domain';
import { z } from 'zod';

import {
  CLINICAL_DOCUMENT_STATUSES,
  printedPatientSchema,
  referralLetterSchema,
  referralSummarySchema,
} from './clinical-documents.ts';
import { actorSchema, isoDaySchema } from './clinical-common.ts';
import { pageQuerySchema, pageSchema } from './pagination.ts';

// Referrals (docs/plans/phase2-referrals.md §3). Reasons, summaries, notes, findings and
// recommendations are PHI: they travel only in bodies. The list's filters are explicit
// query parameters: statuses, urgency, dates and a facility-name fragment, never a patient.

// --- Facilities from the registry cache (plan §1.1 item 2) ------------------------------------

export const referralFacilitySchema = z.object({
  /** The cache row: what a referral stores. */
  id: z.uuid(),
  /** The national registry's identifier. Mono. */
  registryId: z.string(),
  name: z.string(),
  mflCode: z.string().nullable(),
  levelDisplay: z.string().nullable(),
  county: z.string().nullable(),
  subCounty: z.string().nullable(),
  fetchedAt: z.iso.datetime(),
});
export type ReferralFacilityView = z.infer<typeof referralFacilitySchema>;

export const referralFacilityQuerySchema = z.object({
  q: z.string().trim().min(2).max(120),
});

export const referralFacilityListSchema = z.object({ items: z.array(referralFacilitySchema) });

/** A national registry search from the referral form; the matches are cached. */
export const referralRegistrySearchRequestSchema = z
  .object({
    name: z.string().trim().min(2).max(120).optional(),
    mflCode: z.string().trim().min(1).max(40).optional(),
  })
  .refine((q) => q.name !== undefined || q.mflCode !== undefined, {
    message: 'Search by name or MFL code',
  });
export type ReferralRegistrySearchRequest = z.input<typeof referralRegistrySearchRequestSchema>;

// --- Incoming referrals (plan §1.1 item 4) ------------------------------------------------------

export const logIncomingReferralRequestSchema = z.object({
  clientRequestId: z.uuid(),
  /** The referring facility from the cache; null when typed. */
  registryFacilityId: z.uuid().nullable().default(null),
  /** Typed, or the cache's name (the server keeps the cache's words when one is chosen). */
  fromFacilityName: z.string().trim().min(1).max(200),
  fromFacilityMflCode: z.string().trim().min(1).max(20).nullable().default(null),
  referringClinician: z.string().trim().min(1).max(200).nullable().default(null),
  /** Their referral number, as printed on their letter. */
  externalReference: z.string().trim().min(1).max(60).nullable().default(null),
  theirLetterDate: isoDaySchema.nullable().default(null),
  urgency: z.enum(REFERRAL_URGENCIES),
  /** The reason as written on their letter. */
  reason: z.string().trim().min(1).max(2000),
  summaryText: z.string().trim().min(1).max(4000).nullable().default(null),
});
export type LogIncomingReferralRequest = z.input<typeof logIncomingReferralRequestSchema>;

// --- The list (plan §1.1 item 6) ---------------------------------------------------------------

export const referralListQuerySchema = pageQuerySchema.extend({
  direction: z.enum(REFERRAL_DIRECTIONS).default('outgoing'),
  status: z.enum(REFERRAL_STATUSES).optional(),
  urgency: z.enum(REFERRAL_URGENCIES).optional(),
  /** A fragment of the other facility's name or MFL code. */
  facility: z.string().trim().min(2).max(120).optional(),
  /** Referred on or after this East Africa day. */
  from: isoDaySchema.optional(),
  /** Referred on or before this East Africa day. */
  to: isoDaySchema.optional(),
});
export type ReferralListQuery = z.input<typeof referralListQuerySchema>;

/** The export takes the same filters, without paging. */
export const referralExportQuerySchema = referralListQuerySchema.omit({
  limit: true,
  cursor: true,
});

const otherFacilitySchema = z.object({
  name: z.string(),
  mflCode: z.string().nullable(),
  /** False when it was typed (not checked against the registry). */
  fromRegistry: z.boolean(),
});

export const referralRowSchema = z.object({
  id: z.uuid(),
  direction: z.enum(REFERRAL_DIRECTIONS),
  origin: z.enum(REFERRAL_ORIGINS),
  status: z.enum(REFERRAL_STATUSES),
  urgency: z.enum(REFERRAL_URGENCIES),
  /** Outgoing: our letter number. Incoming: their reference, when they gave one. Mono. */
  number: z.string().nullable(),
  patient: z.object({ id: z.uuid(), name: z.string(), patientNumber: z.string() }),
  otherFacility: otherFacilitySchema,
  referredAt: z.iso.datetime(),
  lastEventAt: z.iso.datetime(),
  lastEventKind: z.enum(REFERRAL_EVENT_KINDS),
});
export type ReferralRowView = z.infer<typeof referralRowSchema>;

export const referralListSchema = pageSchema(referralRowSchema);
export type ReferralListView = z.infer<typeof referralListSchema>;

export const patientReferralListSchema = z.object({ items: z.array(referralRowSchema) });

// --- The referral page (plan §1.1 item 7) -----------------------------------------------------

export const referralEventSchema = z.object({
  id: z.uuid(),
  kind: z.enum(REFERRAL_EVENT_KINDS),
  /** When it happened (clinical time). */
  occurredAt: z.iso.datetime(),
  /** When it was recorded. */
  recordedAt: z.iso.datetime(),
  recordedBy: actorSchema,
  channel: z.enum(REFERRAL_CHANNELS).nullable(),
  note: z.string().nullable(),
  feedbackLetterId: z.uuid().nullable(),
});
export type ReferralEventView = z.infer<typeof referralEventSchema>;

const statusChangeSchema = z
  .object({ by: actorSchema, at: z.iso.datetime(), reason: z.string() })
  .nullable();

export const referralFeedbackLetterSchema = z.object({
  id: z.uuid(),
  referralId: z.uuid(),
  visitId: z.uuid(),
  patientId: z.uuid(),
  feedbackNumber: z.string(),
  issuedAt: z.iso.datetime(),
  author: actorSchema,
  authorDisplay: z.string(),
  authorLicenceDisplay: z.string().nullable(),
  toFacilityName: z.string(),
  toFacilityMflCode: z.string().nullable(),
  /** The visit summary at issue; `clinicianText` is "Findings and management". */
  summary: referralSummarySchema,
  recommendations: z.string().nullable(),
  status: z.enum(CLINICAL_DOCUMENT_STATUSES),
  enteredInError: statusChangeSchema,
  printCount: z.number().int().min(0),
});
export type ReferralFeedbackLetterView = z.infer<typeof referralFeedbackLetterSchema>;

export const referralDetailSchema = referralRowSchema.extend({
  visitId: z.uuid(),
  visitNumber: z.string(),
  /** Outgoing: department or clinician referred to. Incoming: the referring clinician. */
  otherService: z.string().nullable(),
  reason: z.string(),
  summaryText: z.string().nullable(),
  theirLetterDate: isoDaySchema.nullable(),
  enteredInError: statusChangeSchema,
  /** Outgoing only: the Phase 1 letter. */
  letter: referralLetterSchema.nullable(),
  /** Incoming only: the feedback letters, newest first. */
  feedbackLetters: z.array(referralFeedbackLetterSchema),
  /** Oldest first. */
  events: z.array(referralEventSchema),
  /** What "Record update" may offer now (REFERRAL_UPDATE_KINDS). */
  allowedUpdates: z.array(z.enum(REFERRAL_UPDATE_KINDS)),
  /** The national referral exchange (plan §4). */
  hie: z.object({ share: z.enum(REFERRAL_HIE_SHARES), words: z.string() }),
});
export type ReferralDetailView = z.infer<typeof referralDetailSchema>;

export const recordReferralUpdateRequestSchema = z.object({
  kind: z.enum(REFERRAL_UPDATE_KINDS),
  /** When it happened; defaults to now. Not in the future, not before the referral. */
  occurredAt: z.iso.datetime({ offset: true }).optional(),
  channel: z.enum(REFERRAL_CHANNELS).nullable().default(null),
  note: z
    .string()
    .trim()
    .max(2000)
    .nullable()
    .default(null)
    .transform((v) => (v === '' ? null : v)),
});
export type RecordReferralUpdateRequest = z.input<typeof recordReferralUpdateRequestSchema>;

// --- Feedback letters (plan §1.1 item 5) --------------------------------------------------------

export const issueFeedbackLetterRequestSchema = z.object({
  clientRequestId: z.uuid(),
  /** The released results to include, from the preview. */
  resultIds: z.array(z.uuid()).max(50).default([]),
  /** "Findings and management", in the clinician's words. */
  clinicianText: z.string().max(4000).default(''),
  recommendations: z
    .string()
    .trim()
    .max(4000)
    .nullable()
    .default(null)
    .transform((v) => (v === '' ? null : v)),
});
export type IssueFeedbackLetterRequest = z.input<typeof issueFeedbackLetterRequestSchema>;

export const referralFeedbackPrintSchema = z.object({
  letter: referralFeedbackLetterSchema,
  /** What the referring facility sent, so they can match the letter to their referral. */
  referral: z.object({
    externalReference: z.string().nullable(),
    theirLetterDate: isoDaySchema.nullable(),
    referredAt: z.iso.datetime(),
    otherService: z.string().nullable(),
  }),
  facility: z.object({ name: z.string() }),
  patient: printedPatientSchema,
  copy: z.number().int().min(1),
});
export type ReferralFeedbackPrintView = z.infer<typeof referralFeedbackPrintSchema>;
